import { useCallback, useEffect, useRef, useState } from 'react'
import { TERMINAL_EXECUTION_STATUSES, type Execution, type ExecutionDetail } from '../api/types.js'

export interface TestRunClient {
  startExecution(pipelineId: string, input?: { fromNodeId?: string }): Promise<Execution>
  execution(id: string): Promise<ExecutionDetail>
}

export interface TestRunButtonProps {
  client: TestRunClient
  pipelineId: string
  /** Выбранный на холсте узел: прогон можно начать с него (FR-UI-004). */
  selectedNodeId?: string | null
  /** Готовый прогон — журналу задачи 20 и панели данных задачи 18. */
  onFinished?: (execution: ExecutionDetail) => void
  /** Период опроса состояния. Настраивается ради проверяемости. */
  pollDelayMs?: number
}

export const DEFAULT_POLL_DELAY_MS = 1000

/** Понятное название состояния: `running` человеку ничего не говорит. */
const STATUS_NAMES: Readonly<Record<string, string>> = {
  running: 'идёт',
  waiting: 'ждёт',
  cancelling: 'отменяется',
  success: 'успешно',
  failed: 'с ошибкой',
  cancelled: 'отменён',
}

function statusNameOf(status: string): string {
  return STATUS_NAMES[status] ?? status
}

/**
 * Тестовый прогон из редактора (FR-UI-010). Состояние опрашивается, а не
 * приходит подпиской: прогон живёт секунды, а вторая транспортная дорога
 * (SSE или сокет) — это ещё одна вещь, которая ломается отдельно, и её
 * пришлось бы держать в живых на всём пути через прокси.
 *
 * Прогон от выбранной ноды начинается с пустого входа (FR-UI-004):
 * настоящих данных взять неоткуда, а придумать их значит показать прогон,
 * которого не было. Повтор с настоящими данными — это повтор с шага из
 * журнала (FR-PIPE-020).
 */
export function TestRunButton({
  client,
  pipelineId,
  selectedNodeId = null,
  onFinished,
  pollDelayMs = DEFAULT_POLL_DELAY_MS,
}: TestRunButtonProps): React.JSX.Element {
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Опрос прекращается при размонтировании: запрос от экрана, которого уже
  // нет, некому и показать.
  const alive = useRef(true)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      alive.current = false
      if (timer.current !== null) clearTimeout(timer.current)
    },
    [],
  )

  /**
   * Читает прогон и, если он ещё идёт, назначает следующее чтение.
   * `delay` в нуле — прогон уже закончился к моменту запуска: его состояние
   * известно, но шаги нужно прочитать, иначе журналу нечего показывать.
   */
  const read = useCallback(
    (executionId: string, delay: number): void => {
      timer.current = setTimeout(() => {
        client
          .execution(executionId)
          .then((detail) => {
            if (!alive.current) return
            setStatus(detail.status)
            if (TERMINAL_EXECUTION_STATUSES.includes(detail.status)) {
              setBusy(false)
              // Отказ прогона показывается как есть: «с ошибкой» без
              // причины отправляет человека искать её в журнале вслепую.
              if (detail.error !== null) setError(detail.error)
              onFinished?.(detail)
              return
            }
            read(executionId, pollDelayMs)
          })
          .catch((failure: unknown) => {
            if (!alive.current) return
            setBusy(false)
            setError(failure instanceof Error ? failure.message : 'Не удалось узнать состояние прогона.')
          })
      }, delay)
    },
    [client, onFinished, pollDelayMs],
  )

  const run = useCallback(
    (input: { fromNodeId?: string }): void => {
      setBusy(true)
      setError(null)
      setStatus(null)
      client
        .startExecution(pipelineId, input)
        .then((execution) => {
          if (!alive.current) return
          setStatus(execution.status)
          // Короткий сценарий успевает закончиться до первого опроса. Ждать
          // период опроса, чтобы прочитать уже готовое, значит держать
          // человека перед пустым журналом на секунду дольше без причины.
          read(execution.id, TERMINAL_EXECUTION_STATUSES.includes(execution.status) ? 0 : pollDelayMs)
        })
        .catch((failure: unknown) => {
          if (!alive.current) return
          setBusy(false)
          setError(failure instanceof Error ? failure.message : 'Прогон не запустился.')
        })
    },
    [client, pipelineId, pollDelayMs, read],
  )

  return (
    // Кнопки оформлены кнопками портала, а не браузерными по умолчанию
    // (FR-ADM-039): серый прямоугольник в блёклой рамке неотличим от
    // выключенного, и главное действие редактора читалось как недоступное.
    <div className="тестовый-прогон">
      <button
        type="button"
        className="btn"
        disabled={busy}
        onClick={() => {
          run({})
        }}
      >
        Тестовый прогон
      </button>

      {selectedNodeId !== null ? (
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => {
            run({ fromNodeId: selectedNodeId })
          }}
        >
          Прогнать от этой ноды
        </button>
      ) : null}

      {status !== null ? (
        <span className="тестовый-прогон__состояние">{`Прогон: ${statusNameOf(status)}`}</span>
      ) : null}
      {error !== null ? (
        <p className="тестовый-прогон__отказ" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}
